import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import gql from 'graphql-tag';
import { useApolloClient, useMutation } from '@apollo/client';
import { useAdyen, useCurrency, useI18n, useLogger, useSelector } from '~Hooks';
import styled from '@emotion/styled';
import axios from 'axios';
import { MethodProps } from '../types';
import {
  CryptoPayment,
  CryptoPaymentVariables,
  InitCryptoPayment,
  InitCryptoPaymentVariables,
} from './__queries__';

const Wrapper = styled.div`
  width: 100vw;
  height: 100vh;
  position: fixed;
  left: 0;
  top: 0;
  background: rgba(0, 0, 0, 0.7);
  z-index: 49;
  animation: fade-in 200ms cubic-bezier(0.215, 0.61, 0.355, 1);
`;

const INIT_PAYMENT = gql`
  mutation InitCryptoPayment($input: CryptoPaymentInput!) {
    initCryptoPayment(input: $input) {
      success
      message
      error
    }
  }
`;

const GET_PAYMENT = gql`
  query CryptoPayment($id: String!) {
    CryptoPayment(id: $id) {
      id
      status
      checkoutUrl
    }
  }
`;

const Crypto: React.FunctionComponent<MethodProps> = ({
  payment,
  onComplete,
  onFail,
  onMetaChange,
  amount,
}) => {
  const currency = useCurrency();
  const logger = useLogger('payment:crypto');
  const timer = useRef<number | undefined>();
  const { i18n } = useI18n();
  const adyen = useAdyen();

  const client = useApolloClient();

  const [initPayment] = useMutation<InitCryptoPayment, InitCryptoPaymentVariables>(INIT_PAYMENT);

  useEffect(() => {
    if (payment.meta.cancelRequested) {
      onFail('Cancelled');
    }
  }, [payment.meta.cancelRequested]);

  useEffect(() => {
    const run = async () => {
      let id = payment.meta?.paymentId;

      if (!id) {
        try {
          // Init payment here
          const response = await initPayment({
            variables: {
              input: {
                amount: amount! / 100,
                currency: currency.code,
                reference: payment.id,
                callbackUrl: window.location.href,
              },
            },
          });

          const data = response.data?.initCryptoPayment;

          console.log(data);

          id = data?.message;

          if (!data?.success) {
            throw new Error(data?.error || 'Failed to initialize payment');
          }

          if (!id) {
            throw new Error(data?.error || 'Failed to initialize payment');
          }

          onMetaChange({
            paymentId: id,
            transactionId: id,
          });
        } catch (e) {
          console.error(e);
          onFail('Failed to initialize payment');
          return;
        }
      }

      const poll = async () => {
        try {
          const { data } = await client.query<CryptoPayment, CryptoPaymentVariables>({
            query: GET_PAYMENT,
            variables: {
              id: id!,
            },
            fetchPolicy: 'network-only',
          });

          const payment = data?.CryptoPayment;

          logger.info('Polling... Status: ' + payment?.status, { payment });

          /**
           * If the payment has been made - remove the QR code
           */
          if (payment?.status === 'NOTICED') {
            onMetaChange({
              qr: null,
            });
          } else if (payment) {
            onMetaChange({
              qr: payment.checkoutUrl,
            });
          }

          if (payment?.status === 'COMPLETED') {
            onComplete(undefined, {
              transactionId: payment.id,
            });
          } else if (payment?.status === 'CANCELLED') {
            onFail('Cancelled');
          } else if (payment?.status === 'ERROR_OCCURRED') {
            onFail('Error occurred');
          } else {
            timer.current = window.setTimeout(poll, 3500);
          }
        } catch (e) {
          console.error(e);
          timer.current = window.setTimeout(poll, 3500);
        }
      };

      poll();
    };

    run();

    return () => {
      clearTimeout(timer.current);
    };
  }, []);

  return null;
};

export default Crypto;
